'use client'

import styles from './LanguageSelector.module.css'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import Flag, { FlagSize } from '@/components/UI/Flag/Flag'
import { flags, Locale, locales } from "@/i18n/constants"
import { useLocale, useTranslations } from "next-intl"
import { usePathname, useRouter } from 'next/navigation'
import ChevronDownIcon from '@/assets/img/Icon_ChevronDown.svg'
import ChevronUpIcon from '@/assets/img/Icon_ChevronUp.svg'
import ChevronRightIcon from '@/assets/img/Icon_ChevronRight.svg'
import RegionIcon from '@/assets/img/Icon_Region.svg'
import Icon, { IconColors, IconTypes } from '../Icon/Icon'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelf } from '@/apiUtilities/queries/useSelf'
import OptionLang from './OptionLang'
import { useWindowSize } from '@/hooks/useWindowsSize'
import { BREAKPOINTS } from '@/utils/constants'
import { useModalStore } from '@/stores/useModalStore'
import LangSelectorModal from '@/components/Modals/LangSelectorModal/LangSelectorModal'
import { useEditSelf } from '@/apiUtilities/mutations/useEditSelf'
import { EditSelfData } from '@/interfaces/api/self/api_request_types'
import Notification from '@/components/Notification/Notification'
import { toast } from 'react-toastify'
import { LangSelectorDirection, LangSelectorKinds, LangSelectorOrder, LangSelectorSizes, LangSelectorTypes } from './langSelectorTypes'
import { useOutsideAlerter } from '@/hooks/useOutsideAlerter'
import { useQueryClient } from '@tanstack/react-query'

dayjs.extend(utc)

interface LanguageSelectorProps extends React.HTMLAttributes<HTMLDivElement> {
	kind?: LangSelectorKinds
	type?: LangSelectorTypes
	size?: LangSelectorSizes
	direction?: LangSelectorDirection
	order?: LangSelectorOrder
}

export default function LanguageSelector ({
	kind = LangSelectorKinds.TEXT,
	type = LangSelectorTypes.WEB,
	size = LangSelectorSizes.LG,
	direction = LangSelectorDirection.DOWN,
	order = LangSelectorOrder.DEFAULT,
	className,
	...rest
}: LanguageSelectorProps) {
	const t = useTranslations('General.languages')
	const te = useTranslations('Errors')
	const tn = useTranslations('Notifications')
	const locale = useLocale()
	const router = useRouter()
	const queryClient = useQueryClient()
	const pathname = usePathname()
	const { data: self, isSuccess: selfIsSuccess } = useSelf()
	const editSelf = useEditSelf() 
	const { openModal, closeModal } = useModalStore()
	const { width } = useWindowSize()
	const [isResponsive, setIsResponsive] = useState<boolean>(true)
	const [selectedLocale, setSelectedLocale] = useState<Locale | null>(type === LangSelectorTypes.WEB ? locale as Locale : null)
	const [showDropdown, setShowDropdown] = useState<boolean>(false)

	const elemRef = useRef<HTMLDivElement>(null)
	useOutsideAlerter(elemRef, () => setShowDropdown(false))

	const selectorIcon = isResponsive 
		? ChevronRightIcon
		: showDropdown ? ChevronUpIcon : ChevronDownIcon

	const sortedLocales: Locale[] = order === LangSelectorOrder.ALPHABETICAL
		? [...locales].sort(compareLocalesTransAlphabetically)
		: [...locales]
	
	/**
	 * Change language by updating the URL path
	 * @param nextLocale Next locale
	 */
	function changeLanguage(nextLocale: string) {
		const segments = pathname.split('/')
		segments[1] = nextLocale // reemplaza el locale (primer segmento)

		const newPath = segments.join('/')
		router.replace(newPath)
	}

	/**
	 * Updates the user's preferred language on the server
	 * 
	 * @returns void
	 */
	function handleUpdatePreferredLang (newPreferredLang: Locale) {
		if(!self) return

		const payload: EditSelfData = {
			id: self.id,
			time: dayjs.utc().format(),
			client_id: '',
			data: {
				preferred_lang: newPreferredLang
			}
		}

		editSelf.mutate(payload,
			{
				onSuccess: async () => {
					await queryClient.invalidateQueries({ queryKey: ['self'] })
					toast.success(<Notification text={tn('changesSaved')} />)
				},
				onError: () => toast.error(<Notification text={te('defaultUnknowError')} />)
			}
		)
	}

	/**
	 * Handle click on a language option in the dropdown
	 * @param nextLocale Next locale to switch to
	 */
	function handleClickOnOption (nextLocale: Locale) {
		if(type === LangSelectorTypes.WEB) changeLanguage(nextLocale)

		if(type === LangSelectorTypes.USER_PREFERRED) {
			setSelectedLocale(nextLocale)
			handleUpdatePreferredLang(nextLocale)
		}

		setShowDropdown(false)
	}

	/**
	 * Locales in alphabetical order based on their translated names
	 * @param a First locale
	 * @param b Second locale
	 * @returns -1 if a < b, 1 if a > b, 0 if equal
	 */
	function compareLocalesTransAlphabetically (a: string, b: string) {
		if(t(a) < t(b)) return -1
		if(t(a) > t(b)) return 1
		return 0	
	}

	/**
	 * Handle click on the selector
	 */
	function handleClickSelector () {
		setShowDropdown(prev => !prev)
		if (isResponsive && selectedLocale !== null) {
			openModal({
				content: <LangSelectorModal
									selectedLocale={selectedLocale}
									localeList={sortedLocales}
									parentKind={kind}
									parentSize={size}
									onLocaleSelect={handleClickOnOption}
									onClose={closeModal}
								/>
			})
		}
	}

	/**
	 * Change whether it is responsive or not 
	 */
	useEffect(() => {
		if(width > 0) setIsResponsive(width < BREAKPOINTS.DESKTOP)
	}, [width])
	
	/**
	 * Set the selected locale based on the user's preferred language if the type is USER_PREFERRED
	 */
	useEffect(() => {
		if(type === LangSelectorTypes.USER_PREFERRED && selfIsSuccess && self.preferred_lang) {
			setSelectedLocale(self.preferred_lang as Locale)
		}
	}, [selfIsSuccess, type])

	return (
		<div ref={elemRef} className={`${styles.langSelectorWrapper} ${styles[size] ?? ''} ${styles[direction] ?? ''} ${className ?? ''}`} {...rest}>
			<button
				type={'button'}
				className={styles.select}
				onClick={handleClickSelector}
				disabled={selectedLocale === null}
				aria-haspopup="listbox"
  			aria-expanded={showDropdown}
				data-testid={'LangSelector'}
			>
				{type === LangSelectorTypes.WEB && 
				<Icon className={styles.regionIcon} icon={RegionIcon} type={IconTypes.EMPTY} color={IconColors.LIGHT} />
				}
				
				<div className={styles.selectedLangWrapper}>
					{selectedLocale && (kind === LangSelectorKinds.FLAG || kind === LangSelectorKinds.TEXT_AND_FLAG) &&
						<Flag locale={selectedLocale as keyof typeof flags} size={FlagSize.SMALL} />
					}
					{(kind === LangSelectorKinds.TEXT || kind === LangSelectorKinds.TEXT_AND_FLAG) &&
						<p className={`bodyTextMediumStrong`}>
							{selectedLocale
								? size === LangSelectorSizes.LG
									? t(selectedLocale)
									: selectedLocale.slice(0, 2).toUpperCase()
								: '---------'
							}
						</p>
					}
				</div>

				<Icon className={styles.dropdownIcon} icon={selectorIcon} type={IconTypes.EMPTY} color={IconColors.LIGHT} />
			</button>

			{!isResponsive && showDropdown && 
			<div
				className={styles.selector}
				role="listbox"
				aria-activedescendant={selectedLocale || undefined}
				data-testid={'LangSelector_Options'}
			>
				{sortedLocales.map(el => 
					<OptionLang
						key={el}
						locale={el}
						selected={el === selectedLocale}
						parentKind={kind}
						parentSize={size}
						setLocale={handleClickOnOption}
					/>
				)}
			</div>
			}
		</div>
	)
}